import { env } from "../../config/env.js";
import type { NextResponse } from "../../utils/server/response-helper.js";

/**
 * Application names used in cookie configuration
 */
export const AppNames = {
  CRM: "CRM",
  QUOTE: "QUOTE"
} as const;

/**
 * Type for application names used in cookie configuration
 */
export type AppName = (typeof AppNames)[keyof typeof AppNames];

/**
 * Generates a custom cookie name for Supabase authentication to prevent
 * cookie conflicts between CRM and Quote applications.
 *
 * @param appName - The application name ('crm' or 'quote')
 * @returns Cookie name in format: sb-{app}-{identifier}-auth-token
 *
 * Examples:
 * - Local: sb-crm-localhost-auth-token, sb-quote-localhost-auth-token
 * - Dev: sb-crm-vomrghiulbmrfvmhlflk-auth-token, sb-quote-vomrghiulbmrfvmhlflk-auth-token
 */
export function getSupabaseCookieName(
  appName: AppName,
  type:
    | "auth-token"
    | "auth-token.0"
    | "auth-token.1"
    | "refresh"
    | "auth-token-code-verifier" = "auth-token"
): string {
  const url = env.NEXT_PUBLIC_SUPABASE_URL;

  // Extract identifier from URL
  const identifier = getIdentifierFromUrl(url);

  // Return format: sb-{app}-{identifier}-auth-token
  // Note: Supabase uses the name as-is, it doesn't append -auth-token
  return `sb-${appName}-${identifier}-${type}`;
}

/**
 * Deletes all Supabase cookies for a given app name
 *
 * @deprecated using signOut will clear the cookies, this is no longer needed.
 * Was due to a mis-configuration of the cookies.
 *
 * @param response - The NextResponse object to delete cookies from
 * @param appName - The app name to delete cookies for
 */
export function deleteSupabaseCookies(
  response: NextResponse,
  appName: AppName
) {
  const cookieNames = [
    getSupabaseCookieName(appName, "auth-token-code-verifier"),
    getSupabaseCookieName(appName, "auth-token"),
    // Possible that the cookie is too large and split into multiple cookies
    getSupabaseCookieName(appName, "auth-token.0"),
    getSupabaseCookieName(appName, "auth-token.1"),
    getSupabaseCookieName(appName, "refresh")
  ];

  for (const name of cookieNames) {
    response.cookies.set({ maxAge: 0, name, path: "/", value: "" });
  }
}

/**
 * Extracts the identifier from the Supabase URL
 * @param url - The Supabase URL
 * @returns The identifier from the URL
 */
function getIdentifierFromUrl(url: string): string {
  // Local: http://localhost:54321 → localhost
  if (url.includes("localhost")) {
    return "localhost";
  }

  // Supabase: https://vomrghiulbmrfvmhlflk.supabase.co → vomrghiulbmrfvmhlflk
  const match = url.match(/https:\/\/([^.]+)\.supabase\.co/);
  return match?.[1] ?? "default";
}
